import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Zap, Database, BarChart3, Clock, Trophy, Layers, Play, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const SAMPLE_QUESTIONS = [
  "What is double time?",
  "How many bereavement days for step-parent?",
  "What are the ARCOS callout rules?",
  "What is the meal money allowance?",
  "Can management deny my vacation?",
  "What does Article 12 say?",
  "PPE requirements for linemen",
  "What is the grievance procedure?",
];

const BENCHMARK_QUESTIONS = [
  "What is double time?",
  "What article covers overtime?",
  "What does the contract say about meal allowance?",
  "Can I have representation?",
  "What is call out pay?",
  "What article covers seniority?",
  "How does vacation pick work?",
  "What does the contract say about holidays?",
];

const WORKER_BENCHMARK_QUESTIONS = [
  "can they force me in",
  "do I get meal money",
  "what if they write me up",
  "can I ask for a steward",
  "what's dt",
  "what article is holidays",
  "called in pay",
  "vacation pick rules",
];

function ScoreBar({ value, max = 1, color = '#3b82f6', label }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      {label && <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 60 }}>{label}</span>}
      <div style={{ flex: 1, height: 6, background: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: 11, color: '#cbd5e1', minWidth: 40, textAlign: 'right' }}>{value.toFixed(3)}</span>
    </div>
  );
}

function ChunkCard({ chunk, rank, engine }) {
  const isHybrid = engine === 'hybrid';
  const borderColor = isHybrid ? '#8b5cf6' : '#3b82f6';
  return (
    <div
      data-testid={`${engine}-chunk-${rank}`}
      style={{
        background: '#0f172a',
        border: `1px solid ${borderColor}33`,
        borderRadius: 10,
        padding: 14,
        marginBottom: 10,
        borderLeft: `3px solid ${borderColor}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: borderColor }}>#{rank + 1}</span>
        <span style={{ fontSize: 11, color: '#64748b' }}>{chunk.book_title}</span>
        <span style={{ fontSize: 11, color: '#64748b' }}>p.{chunk.approx_page || '?'}</span>
      </div>
      <p style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, margin: '0 0 10px 0' }}>
        {chunk.content_preview}
      </p>
      <ScoreBar value={chunk.score} color={borderColor} label="Score" />
      {isHybrid && chunk.scores_detail && (
        <>
          <ScoreBar value={chunk.scores_detail.keyword} color="#f59e0b" label="Keyword" />
          <ScoreBar value={chunk.scores_detail.vector} color="#10b981" label="Vector" />
          <ScoreBar value={chunk.scores_detail.reference} color="#ef4444" label="Ref" />
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = '#3b82f6' }) {
  return (
    <div style={{
      background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10,
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flex: 1
    }}>
      <div style={{ background: `${color}22`, borderRadius: 8, padding: 8 }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>{value}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: '#64748b' }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function RetrievalComparePage() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [topK, setTopK] = useState(10);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [activeBenchmarkType, setActiveBenchmarkType] = useState(null);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/hybrid/compare/history?limit=20`);
      setHistory(res.data);
    } catch {
      /* ignore */
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const runComparison = async (q) => {
    const query = q || question;
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/hybrid/compare`, { question: query, top_k: topK });
      setResult(res.data);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Comparison failed');
    }
    setLoading(false);
  };

  const runBenchmark = async (type = 'formal') => {
    setBenchmarkLoading(true);
    setBenchmark(null);
    setError('');
    setActiveBenchmarkType(type);
    const questions = type === 'worker' ? WORKER_BENCHMARK_QUESTIONS : BENCHMARK_QUESTIONS;
    try {
      const res = await axios.post(`${API}/hybrid/compare/batch`, { questions, top_k: topK });
      setBenchmark(res.data);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Benchmark failed');
    }
    setBenchmarkLoading(false);
  };

  return (
    <div data-testid="retrieval-compare-page" style={{ minHeight: '100vh', background: '#030712', color: '#e2e8f0', padding: '24px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          data-testid="back-button"
          onClick={() => navigate('/admin')}
          style={{ background: '#1e293b', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>Retrieval A/B Compare</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Side-by-side: Legacy (TF-IDF) vs Hybrid v2 (Keyword + Vector + Reference)</p>
        </div>
        <span style={{ marginLeft: 'auto', background: '#f59e0b18', border: '1px solid #f59e0b40', color: '#fbbf24', padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }} data-testid="benchmark-mode-badge">
          BENCHMARK MODE
        </span>
      </div>

      {/* Benchmark notice */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 12, color: '#94a3b8' }} data-testid="benchmark-notice">
        Admin-only benchmark infrastructure. Not used by live chat traffic. Live chat uses <span style={{ color: '#a78bfa' }}>/api/hybrid/ask</span> exclusively.
      </div>

      {/* Search Input */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 14, top: 13, color: '#64748b' }} />
          <input
            data-testid="compare-question-input"
            type="text"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runComparison()}
            placeholder="Ask a union question to compare both engines..."
            style={{
              width: '100%', padding: '10px 14px 10px 38px', background: '#0f172a',
              border: '1px solid #334155', borderRadius: 10, color: '#e2e8f0', fontSize: 14,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <select
          data-testid="compare-topk-select"
          value={topK}
          onChange={e => setTopK(Number(e.target.value))}
          style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, color: '#e2e8f0', padding: '0 12px', fontSize: 13 }}
        >
          {[5, 10, 15, 20].map(v => <option key={v} value={v}>Top {v}</option>)}
        </select>
        <button
          data-testid="compare-run-button"
          onClick={() => runComparison()}
          disabled={loading || !question.trim()}
          style={{
            background: loading ? '#334155' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            border: 'none', borderRadius: 10, padding: '10px 20px', color: '#fff',
            fontWeight: 600, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <Zap size={15} />
          {loading ? 'Running...' : 'Compare'}
        </button>
        <button
          data-testid="benchmark-run-button"
          onClick={() => runBenchmark('formal')}
          disabled={benchmarkLoading}
          style={{
            background: benchmarkLoading && activeBenchmarkType === 'formal' ? '#334155' : 'linear-gradient(135deg, #10b981, #059669)',
            border: 'none', borderRadius: 10, padding: '10px 16px', color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: benchmarkLoading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <Play size={15} />
          {benchmarkLoading && activeBenchmarkType === 'formal' ? 'Running...' : 'Formal Benchmark'}
        </button>
        <button
          data-testid="worker-benchmark-run-button"
          onClick={() => runBenchmark('worker')}
          disabled={benchmarkLoading}
          style={{
            background: benchmarkLoading && activeBenchmarkType === 'worker' ? '#334155' : 'linear-gradient(135deg, #f59e0b, #d97706)',
            border: 'none', borderRadius: 10, padding: '10px 16px', color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: benchmarkLoading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <Play size={15} />
          {benchmarkLoading && activeBenchmarkType === 'worker' ? 'Running...' : 'Worker Phrasing'}
        </button>
      </div>

      {/* Sample Questions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
        {SAMPLE_QUESTIONS.map((sq, i) => (
          <button
            key={i}
            data-testid={`sample-q-${i}`}
            onClick={() => { setQuestion(sq); runComparison(sq); }}
            style={{
              background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
              color: '#94a3b8', fontSize: 11, padding: '5px 12px', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.target.style.borderColor = '#6366f1'; e.target.style.color = '#c4b5fd'; }}
            onMouseLeave={e => { e.target.style.borderColor = '#334155'; e.target.style.color = '#94a3b8'; }}
          >
            {sq}
          </button>
        ))}
      </div>

      {error && <div data-testid="compare-error" style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 10, padding: 14, color: '#fca5a5', marginBottom: 16 }}>{error}</div>}

      {/* Benchmark Results */}
      {benchmark && (
        <div data-testid="benchmark-results" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <BarChart3 size={16} color="#10b981" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>Benchmark Scorecard</h2>
            <span style={{
              marginLeft: 12, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
              background: benchmark.all_thresholds_pass ? '#10b98120' : '#ef444420',
              color: benchmark.all_thresholds_pass ? '#34d399' : '#fca5a5',
              border: `1px solid ${benchmark.all_thresholds_pass ? '#10b98140' : '#ef444440'}`,
            }} data-testid="benchmark-overall-status">
              {benchmark.all_thresholds_pass ? 'ALL PASS' : 'THRESHOLD FAIL'}
            </span>
          </div>

          {/* Scorecard Stats */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard icon={Trophy} label="Win Rate" value={`${benchmark.hybrid_win_rate}%`} sub={`${benchmark.hybrid_wins}W / ${benchmark.legacy_wins}L / ${benchmark.ties}T`} color={benchmark.hybrid_win_rate >= 80 ? '#10b981' : '#ef4444'} />
            <StatCard icon={Clock} label="Avg Hybrid Latency" value={`${benchmark.avg_hybrid_latency_ms}ms`} sub={`Legacy: ${benchmark.avg_legacy_latency_ms}ms`} color={benchmark.avg_hybrid_latency_ms < 300 ? '#10b981' : '#ef4444'} />
            <StatCard icon={Database} label="Relevant Top Chunks" value={`${benchmark.relevant_top_chunks}/${benchmark.total_questions}`} sub="Score >= 0.3" color={benchmark.relevant_top_chunks >= 6 ? '#10b981' : '#ef4444'} />
          </div>

          {/* Threshold Checks */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {Object.entries(benchmark.thresholds || {}).map(([key, t]) => (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                background: t.pass ? '#10b98110' : '#ef444410',
                border: `1px solid ${t.pass ? '#10b98130' : '#ef444430'}`,
                borderRadius: 8, fontSize: 12,
              }}>
                {t.pass ? <CheckCircle size={14} color="#34d399" /> : <XCircle size={14} color="#fca5a5" />}
                <span style={{ color: '#94a3b8' }}>{key.replace(/_/g, ' ')}:</span>
                <span style={{ color: t.pass ? '#34d399' : '#fca5a5', fontWeight: 600 }}>{t.actual}</span>
                <span style={{ color: '#475569' }}>({t.target})</span>
              </div>
            ))}
          </div>

          {/* Per-question table */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#1e293b' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Question</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Winner</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Hybrid Score</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Legacy Score</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Hybrid Top Chunk</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Latency (H/L)</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {benchmark.results.map((r, i) => {
                  const relevant = r.hybrid_top_score >= 0.3;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid #1e293b' }} data-testid={`benchmark-row-${i}`}>
                      <td style={{ padding: '10px 14px', color: '#cbd5e1', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.question}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          background: r.winner === 'hybrid_v2' ? '#8b5cf622' : r.winner === 'legacy' ? '#3b82f622' : '#64748b22',
                          color: r.winner === 'hybrid_v2' ? '#c4b5fd' : r.winner === 'legacy' ? '#93c5fd' : '#94a3b8',
                        }}>
                          {r.winner === 'hybrid_v2' ? 'Hybrid' : r.winner === 'legacy' ? 'Legacy' : r.winner === 'error' ? 'Error' : 'Tie'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#a78bfa', fontFamily: 'monospace' }}>{r.hybrid_top_score?.toFixed(3)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#60a5fa', fontFamily: 'monospace' }}>{r.legacy_top_score?.toFixed(3)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>{r.hybrid_top_chunk}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8' }}>{r.hybrid_latency_ms}ms/{r.legacy_latency_ms}ms</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        {relevant ? <CheckCircle size={14} color="#34d399" /> : <AlertTriangle size={14} color="#f59e0b" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Benchmark loading overlay */}
      {benchmarkLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Running Full Benchmark ({BENCHMARK_QUESTIONS.length} questions)...</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>This may take 30-60 seconds</div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Summary Stats */}
          <div data-testid="compare-stats" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard icon={Trophy} label="Winner" value={result.winner === 'hybrid_v2' ? 'Hybrid v2' : result.winner === 'legacy' ? 'Legacy' : 'Tie'} sub={result.winner_reason} color={result.winner === 'hybrid_v2' ? '#8b5cf6' : result.winner === 'legacy' ? '#3b82f6' : '#64748b'} />
            <StatCard icon={Layers} label="Chunk Overlap" value={result.comparison.overlap_count} sub={`of ${topK} per engine`} color="#f59e0b" />
            <StatCard icon={Database} label="Gold Chunks" value={`${result.comparison.hybrid_gold_chunks} / ${result.comparison.legacy_gold_chunks}`} sub="Hybrid / Legacy" color="#10b981" />
            <StatCard icon={Clock} label="Latency" value={`${result.timing.hybrid_ms}ms`} sub={`Legacy: ${result.timing.legacy_ms}ms`} color="#06b6d4" />
          </div>

          {/* Score Comparison Bar */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 10 }}>Avg Score Comparison</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#3b82f6', marginBottom: 4 }}>Legacy: {result.comparison.legacy_score_avg.toFixed(4)} (max: {result.comparison.legacy_score_max.toFixed(4)})</div>
                <div style={{ height: 8, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(result.comparison.legacy_score_avg * 100, 100)}%`, height: '100%', background: '#3b82f6', borderRadius: 4 }} />
                </div>
              </div>
              <span style={{ color: '#475569', fontWeight: 700 }}>vs</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#8b5cf6', marginBottom: 4 }}>Hybrid: {result.comparison.hybrid_score_avg.toFixed(4)} (max: {result.comparison.hybrid_score_max.toFixed(4)})</div>
                <div style={{ height: 8, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(result.comparison.hybrid_score_avg * 100, 100)}%`, height: '100%', background: '#8b5cf6', borderRadius: 4 }} />
                </div>
              </div>
            </div>
            {result.hybrid?.query_analysis?.expanded_terms?.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#64748b' }}>
                Synonym expansion: {result.hybrid.query_analysis.expanded_terms.slice(0, 10).join(', ')}
              </div>
            )}
          </div>

          {/* Side-by-side Results */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Legacy Column */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px', background: '#3b82f622', borderRadius: 8, border: '1px solid #3b82f633' }}>
                <Database size={15} color="#3b82f6" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>Legacy Engine</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>{result.legacy.result_count} chunks</span>
              </div>
              {result.legacy.results.map((c, i) => <ChunkCard key={i} chunk={c} rank={i} engine="legacy" />)}
              {result.legacy.results.length === 0 && <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 24 }}>No results</div>}
            </div>

            {/* Hybrid Column */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px', background: '#8b5cf622', borderRadius: 8, border: '1px solid #8b5cf633' }}>
                <Zap size={15} color="#8b5cf6" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#8b5cf6' }}>Hybrid v2 Engine</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>{result.hybrid.result_count} chunks | kw={result.hybrid.strategy_counts?.keyword || 0} vec={result.hybrid.strategy_counts?.vector || 0} ref={result.hybrid.strategy_counts?.reference || 0}</span>
              </div>
              {result.hybrid.results.map((c, i) => <ChunkCard key={i} chunk={c} rank={i} engine="hybrid" />)}
              {result.hybrid.results.length === 0 && <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 24 }}>No results</div>}
            </div>
          </div>
        </>
      )}

      {/* Comparison History */}
      {history?.logs?.length > 0 && (
        <div data-testid="compare-history" style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <BarChart3 size={16} color="#94a3b8" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>Comparison History</h2>
            {history.stats && (
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
                Hybrid wins: {history.stats.hybrid_wins} ({history.stats.hybrid_win_rate}%) | Legacy: {history.stats.legacy_wins} | Ties: {history.stats.ties}
              </span>
            )}
          </div>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#1e293b' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Question</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Winner</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Gold (H/L)</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Avg Score (H/L)</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Latency</th>
                </tr>
              </thead>
              <tbody>
                {history.logs.map((log, i) => (
                  <tr
                    key={i}
                    style={{ borderTop: '1px solid #1e293b', cursor: 'pointer' }}
                    onClick={() => { setQuestion(log.question); runComparison(log.question); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#1e293b'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    data-testid={`history-row-${i}`}
                  >
                    <td style={{ padding: '10px 14px', color: '#cbd5e1', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.question}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: log.winner === 'hybrid_v2' ? '#8b5cf622' : log.winner === 'legacy' ? '#3b82f622' : '#64748b22',
                        color: log.winner === 'hybrid_v2' ? '#c4b5fd' : log.winner === 'legacy' ? '#93c5fd' : '#94a3b8',
                      }}>
                        {log.winner === 'hybrid_v2' ? 'Hybrid' : log.winner === 'legacy' ? 'Legacy' : 'Tie'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8' }}>{log.hybrid_gold}/{log.legacy_gold}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8' }}>{log.hybrid_score_avg?.toFixed(3)}/{log.legacy_score_avg?.toFixed(3)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8' }}>{log.hybrid_ms}ms/{log.legacy_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(3, 7, 18, 0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 50,
        }}>
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Running both engines...</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>This may take a few seconds</div>
          </div>
        </div>
      )}
    </div>
  );
}
